import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { SellerStepProps, getHowHeardOptions } from '@/types/seller-registration.types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

export const SellerStepOne: React.FC<SellerStepProps> = ({
  formData,
  updateFormData,
  errors,
  clearError,
  t,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const howHeardOptions = getHowHeardOptions(t);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-[#1D314D] mb-2">
          {t.steps.name} 1: {t.step1.sectionTitle}
        </h3>
      </div>

      <div className="space-y-4">
        {/* First Name & Last Name - Same Line */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-[#1D314D] font-semibold">
              {t.step1.firstName} *
            </Label>
            <Input
              id="firstName"
              type="text"
              placeholder={t.step1.firstNamePlaceholder}
              value={formData.firstName}
              onChange={(e) => {
                updateFormData({ firstName: e.target.value });
                clearError('firstName');
              }}
              className="bg-white text-black border-gray-300"
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-[#1D314D] font-semibold">
              {t.step1.lastName} *
            </Label>
            <Input
              id="lastName"
              type="text"
              placeholder={t.step1.lastNamePlaceholder}
              value={formData.lastName}
              onChange={(e) => {
                updateFormData({ lastName: e.target.value });
                clearError('lastName');
              }}
              className="bg-white text-black border-gray-300"
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#1D314D] font-semibold">
            {t.step1.email} *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder={t.step1.emailPlaceholder}
            value={formData.email}
            onChange={(e) => {
              updateFormData({ email: e.target.value });
              clearError('email');
            }}
            className="bg-white text-black border-gray-300"
          />
          {errors.email && (
            <p className="text-red-500 text-sm">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#1D314D] font-semibold">
            {t.step1.phone} *
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder={t.step1.phonePlaceholder}
            value={formData.phone}
            onChange={(e) => {
              updateFormData({ phone: e.target.value });
              clearError('phone');
            }}
            className="bg-white text-black border-gray-300"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm">{errors.phone}</p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-[#1D314D] font-semibold">
            {t.step1.password} *
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t.step1.passwordPlaceholder}
              value={formData.password}
              onChange={(e) => {
                updateFormData({ password: e.target.value });
                clearError('password');
              }}
              className="bg-white text-black border-gray-300 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-sm">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-[#1D314D] font-semibold">
            {t.step1.confirmPassword} *
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t.step1.confirmPasswordPlaceholder}
              value={formData.confirmPassword}
              onChange={(e) => {
                updateFormData({ confirmPassword: e.target.value });
                clearError('confirmPassword');
              }}
              className="bg-white text-black border-gray-300 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
          )}
        </div>

        {/* How Heard - Multi-select */}
        <div className="space-y-2">
          <Label className="text-[#1D314D] font-semibold">
            {t.step1.howHeard}
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-300">
            {howHeardOptions.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm text-[#1D314D]">
                <Checkbox
                  checked={formData.howHeard.includes(option.value)}
                  onCheckedChange={(checked) => {
                    const updated = checked
                      ? [...formData.howHeard, option.value]
                      : formData.howHeard.filter((v) => v !== option.value);
                    updateFormData({ howHeard: updated });
                    clearError('howHeard');
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
          {errors.howHeard && (
            <p className="text-red-500 text-sm">{errors.howHeard}</p>
          )}
        </div>
      </div>
    </div>
  );
};
